/**
 * Arrival time that cannot contradict the trip duration.
 *
 * Some `schedules` rows carry `arrival_time` equal to `departure_time` — the
 * Feni to Maniknagar 05:22 row, for one — which renders as
 * "05:22 Feni ---5h 00m--> 05:22 Maniknagar": a coach that arrives the minute
 * it leaves. This lives in lib/ rather than in one service because bookings and
 * search read the same schedule rows by different paths, and fixing only the
 * search path left the same nonsense on the dashboard, the e-ticket and Manage
 * Booking.
 *
 * Only the impossible case is corrected: zero elapsed time on a trip that has a
 * real duration. A stated arrival *earlier* than departure is left exactly as
 * it is, because that is what a legitimate overnight coach looks like.
 */
export function resolveArrivalTime(
  departure: string | null | undefined,
  arrival: string | null | undefined,
  durationMinutes: number | null | undefined,
): string {
  const arrivalShort = (arrival || '').substring(0, 5);
  const departureShort = (departure || '').substring(0, 5);
  const duration = durationMinutes ?? 0;

  const toMinutes = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
  };

  const dep = toMinutes(departureShort);
  const arr = toMinutes(arrivalShort);
  if (dep === null || arr === null || duration <= 0) return arrivalShort;

  const elapsed = (arr - dep + 1440) % 1440;
  if (elapsed !== 0) return arrivalShort;

  const derived = (dep + duration) % 1440;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(Math.floor(derived / 60))}:${pad(derived % 60)}`;
}

/**
 * The absolute instant a service leaves, from the date and wall-clock time the
 * schedule is stored in. Bangladesh is UTC+06:00 year round with no daylight
 * saving, so the offset is safe to pin.
 *
 * Returns null when either part is missing or malformed — callers must not turn
 * that into `new Date(NaN)` and render "NaN minutes".
 */
export function departureInstant(
  travelDate: string,
  departureTime: string | null | undefined,
): number | null {
  const time = (departureTime || '').substring(0, 5);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(travelDate) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    return null;
  }
  const ms = Date.parse(`${travelDate}T${time}:00+06:00`);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Whether a scheduled departure is still purchasable in Bangladesh time.
 *
 * Schedules store a local DATE plus TIME, not a timestamptz. Bangladesh has a
 * fixed UTC+06:00 offset, so composing the stored values with that explicit
 * offset avoids depending on the browser or server's local timezone.
 */
export function isDepartureBookable(
  travelDate: string,
  departureTime: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const time = (departureTime || '').substring(0, 5);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(travelDate) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    return false;
  }

  const departureMs = Date.parse(`${travelDate}T${time}:00+06:00`);
  return Number.isFinite(departureMs) && departureMs > now.getTime();
}
